import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Heading,
  IconButton,
  Input,
  Stack,
  Text,
  useToast,
} from "@chakra-ui/react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { format } from "date-fns";
import { TbTrash, TbFileDescription, TbUpload } from "react-icons/tb";

import {
  deleteEmployeeDocumentAction,
  uploadEmployeeDocumentAction,
} from "../../../../../actions/employeeActions";
import {
  EMPLOYEE_DOCUMENT_DELETE_RESET,
  EMPLOYEE_DOCUMENT_UPLOAD_RESET,
} from "../../../../../constants/employeeConstants";
import useThemeColors from "../../../../../hooks/useThemeColors";

const formatFileSize = (size) => {
  if (typeof size !== "number" || Number.isNaN(size)) {
    return "-";
  }

  if (size === 0) {
    return "0 B";
  }

  const units = ["B", "KB", "MB", "GB"];
  let value = size;
  let unitIndex = 0;

  while (value >= 1024 && unitIndex < units.length - 1) {
    value /= 1024;
    unitIndex += 1;
  }

  const precision = value < 10 && unitIndex > 0 ? 1 : 0;
  return `${value.toFixed(precision)} ${units[unitIndex]}`;
};

const formatDate = (value) => {
  if (!value) {
    return "";
  }

  try {
    return format(new Date(value), "yyyy-MM-dd");
  } catch (_error) {
    return "";
  }
};

const EmployeeDocuments = ({
  slug,
  employeeId,
  employee,
  onDocumentsUpdated,
}) => {
  const dispatch = useDispatch();
  const toast = useToast();
  const { t } = useTranslation();
  const fileInputRef = useRef(null);
  const { whiteMode, backgroundLightMode, newBorderMode, textSecondaryMode } =
    useThemeColors();

  const [documents, setDocuments] = useState(employee?.documents || []);
  const [deletingId, setDeletingId] = useState(null);

  const uploadState = useSelector((state) => state.employeeDocumentUpload);
  const {
    loading: uploadLoading,
    error: uploadError,
    success: uploadSuccess,
    document: uploadedDocument,
  } = uploadState;

  const deleteState = useSelector((state) => state.employeeDocumentDelete);
  const {
    loading: deleteLoading,
    error: deleteError,
    success: deleteSuccess,
    documentId: deletedDocumentId,
  } = deleteState;

  useEffect(() => {
    setDocuments(employee?.documents || []);
  }, [employee?.documents]);

  useEffect(() => {
    if (uploadSuccess && uploadedDocument) {
      setDocuments((prev) => [...prev, uploadedDocument]);
      toast({
        title: t("employee.documentUploadSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      if (typeof onDocumentsUpdated === "function") {
        onDocumentsUpdated();
      }
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      dispatch({ type: EMPLOYEE_DOCUMENT_UPLOAD_RESET });
    } else if (uploadError) {
      toast({
        title: t("employee.documentUploadError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
      dispatch({ type: EMPLOYEE_DOCUMENT_UPLOAD_RESET });
    }
  }, [
    dispatch,
    onDocumentsUpdated,
    toast,
    t,
    uploadError,
    uploadSuccess,
    uploadedDocument,
  ]);

  useEffect(() => {
    if (deleteSuccess && deletedDocumentId) {
      setDocuments((prev) =>
        prev.filter((document) => document._id !== deletedDocumentId)
      );
      toast({
        title: t("employee.documentDeleteSuccess"),
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      setDeletingId(null);
      if (typeof onDocumentsUpdated === "function") {
        onDocumentsUpdated();
      }
      dispatch({ type: EMPLOYEE_DOCUMENT_DELETE_RESET });
    } else if (deleteError) {
      toast({
        title: t("employee.documentDeleteError"),
        status: "error",
        duration: 3000,
        isClosable: true,
      });
      setDeletingId(null);
      dispatch({ type: EMPLOYEE_DOCUMENT_DELETE_RESET });
    }
  }, [
    deleteError,
    deleteSuccess,
    deletedDocumentId,
    dispatch,
    onDocumentsUpdated,
    toast,
    t,
  ]);

  const sortedDocuments = useMemo(() => {
    if (!Array.isArray(documents)) {
      return [];
    }

    return [...documents].sort((a, b) => {
      const aDate = a?.uploadedAt ? new Date(a.uploadedAt).getTime() : 0;
      const bDate = b?.uploadedAt ? new Date(b.uploadedAt).getTime() : 0;
      return bDate - aDate;
    });
  }, [documents]);

  const handleSelectFile = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  const handleFileChange = (event) => {
    const file = event.target.files?.[0];
    if (!file) {
      return;
    }

    dispatch(uploadEmployeeDocumentAction(slug, employeeId, file));
  };

  const handleDelete = (documentId) => {
    setDeletingId(documentId);
    dispatch(deleteEmployeeDocumentAction(slug, employeeId, documentId));
  };

  return (
    <Box
      bg={whiteMode}
      borderRadius="1.5rem"
      border={`1px solid ${newBorderMode}`}
      mt="2rem"
    >
      <Box p={["1.5rem", "2rem"]} borderBottom={`1px solid ${newBorderMode}`}>
        <Flex direction={["column", "row"]} gap="1rem" justify="space-between">
          <Box>
            <Heading size="md" mb={1}>
              {t("employee.documentsTab")}
            </Heading>
            <Text fontSize="sm" color={textSecondaryMode}>
              {t("employee.uploadDocument")}
            </Text>
          </Box>
          <Box>
            <Button
              leftIcon={<TbUpload size={18} />}
              onClick={handleSelectFile}
              isLoading={uploadLoading}
              loadingText={t("common.uploading", "Uploading")}
              variant="outline"
              size="sm"
            >
              {t("employee.uploadDocument")}
            </Button>
            <Input
              ref={fileInputRef}
              type="file"
              display="none"
              onChange={handleFileChange}
              accept=".pdf,.doc,.docx,.xls,.xlsx,.csv,.png,.jpg,.jpeg,.gif,.txt"
            />
          </Box>
        </Flex>
      </Box>

      <Box p={["1.5rem", "2rem"]} bg={backgroundLightMode} borderRadius="0 0 1.5rem 1.5rem">
        {sortedDocuments.length === 0 ? (
          <Flex
            direction="column"
            align="center"
            justify="center"
            minH="160px"
            border={`1px dashed ${newBorderMode}`}
            borderRadius="1rem"
            p="2rem"
          >
            <Text fontSize="sm" color={textSecondaryMode} textAlign="center">
              {t("employee.noDocuments")}
            </Text>
          </Flex>
        ) : (
          <Stack spacing={3}>
            {sortedDocuments.map((document) => (
              <Flex
                key={document._id}
                align="center"
                justify="space-between"
                border={`1px solid ${newBorderMode}`}
                borderRadius="1rem"
                p="1rem"
                bg={whiteMode}
                gap="1rem"
                direction={["column", "row"]}
              >
                <Flex align="center" gap="1rem" w="100%">
                  <Flex
                    align="center"
                    justify="center"
                    borderRadius="0.75rem"
                    bg={backgroundLightMode}
                    border={`1px solid ${newBorderMode}`}
                    w="48px"
                    h="48px"
                    flexShrink={0}
                  >
                    <TbFileDescription size={22} />
                  </Flex>
                  <Box flex={1} overflow="hidden">
                    <Text fontWeight="500" isTruncated>
                      {document.originalName || "Document"}
                    </Text>
                    <Text fontSize="sm" color={textSecondaryMode}>
                      {formatFileSize(document.fileSize)}
                      {document.uploadedAt
                        ? ` • ${formatDate(document.uploadedAt)}`
                        : ""}
                    </Text>
                  </Box>
                </Flex>
                <Flex align="center" gap="0.5rem" w={["100%", "auto"]} justify={["flex-start", "flex-end"]}>
                  <Button
                    as="a"
                    href={document.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="sm"
                    variant="outline"
                  >
                    {t("employee.viewDocument")}
                  </Button>
                  <IconButton
                    aria-label={t("common.delete", "Delete")}
                    icon={<TbTrash size={18} />}
                    size="sm"
                    variant="ghost"
                    onClick={() => handleDelete(document._id)}
                    isLoading={deleteLoading && deletingId === document._id}
                  />
                </Flex>
              </Flex>
            ))}
          </Stack>
        )}
      </Box>
    </Box>
  );
};

export default EmployeeDocuments;
